// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
//
// Dialog displaying confirmed and pending serials for a shipment line item.
// Confirmed serials are linked to their associated asset pages via lazy-loaded
// asset key resolution that fires only when the dialog opens. The list is
// virtualized so inbound/outbound shipments with tens of thousands of serials
// render without freezing the browser.
import * as Sentry from '@sentry/react';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { Link } from '@/components/link';
import {
  AccessTimeOutlined,
  CheckOutlined,
  Close as CloseIcon,
  Download as DownloadIcon,
  SearchOutlined,
} from '@/constants/hardfin-standard-icons';
import { resolveSerials } from '@/shipment/api/resolve-serials';
import {
  buildCSVContent,
  escapeCSV,
  generateCSVFilename,
  triggerCSVDownload,
} from '@/utilities/csv-export';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

const MAX_INLINE_SERIAL_LINK_RESOLUTION = 500;

// Delay before an in-progress search is applied. Debouncing keeps the O(n)
// filter off every keystroke, which matters when a shipment carries tens of
// thousands of serials.
const SEARCH_DEBOUNCE_MS = 300;

// Virtualized grid geometry. Three serials per row matches the serial
// collection drawer; the row height and max list height mirror the report
// snapshot tables so scroll behavior is consistent across the app.
const SERIALS_PER_ROW = 3;
const ROW_HEIGHT_PX = 36;
const MAX_LIST_HEIGHT_PX = 400;
const OVERSCAN_ROW_COUNT = 10;

// Serials shorter than this fit the cell without truncation, so no tooltip
const SERIAL_TOOLTIP_MIN_LENGTH = 11;

type SerialKind = 'confirmed' | 'pending';

interface SerialEntry {
  serial: string;
  kind: SerialKind;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  serials: string[];
  pendingSerials?: string[];
  itemName: string;
}

/**
 * Dialog displaying the list of serials for a shipment item.
 * When confirmed serials exist, only those are shown (green). Otherwise
 * pending serials are shown (amber), which occurs for in-progress shipments
 * before the FIN milestone.
 */
export function ShipmentSerialsDialog(props: Props) {
  const [searchQuery, setSearchQuery] = useState('');
  // Debounced mirror of searchQuery that actually drives the filter
  const [debouncedQuery, setDebouncedQuery] = useState('');
  // Map of serial number -> asset key, populated lazily when the dialog opens
  const [assetKeyMap, setAssetKeyMap] = useState<Record<string, string>>({});
  const [
    didResolverFallbackToAssetSearch,
    setDidResolverFallbackToAssetSearch,
  ] = useState(false);

  // Once confirmed serials exist, pending serials are stale and hidden
  // entirely (count, notice, list, and CSV export). Pending is only shown
  // for pre-FIN shipments where no confirmed serials have been written yet.
  const pendingSerials = useMemo(
    () => (props.serials.length > 0 ? [] : (props.pendingSerials ?? [])),
    [props.serials, props.pendingSerials]
  );
  const shouldFallbackToAssetSearch =
    props.serials.length > MAX_INLINE_SERIAL_LINK_RESOLUTION ||
    didResolverFallbackToAssetSearch;

  // Lazily resolve confirmed serials to asset keys when the dialog opens
  useEffect(() => {
    if (
      !props.isOpen ||
      props.serials.length === 0 ||
      shouldFallbackToAssetSearch
    ) {
      return;
    }

    let cancelled = false;
    resolveSerials(props.serials)
      .then((result) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const entry of result.data.assets) {
          map[entry.serial.toLowerCase()] = entry.key;
        }
        setAssetKeyMap(map);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const apiError = handleAPIError(error as AxiosError);
        if (
          apiError.apiStatusCode ===
          ERROR_CODE_MAPPING.asset_resolve_serial_limit_exceeded
        ) {
          setAssetKeyMap({});
          setDidResolverFallbackToAssetSearch(true);
          return;
        }

        // If resolution fails, display serials as plain text
        // This should not happen, so capture the real error in Sentry
        Sentry.captureException(error, {
          tags: {
            errorType: 'shipment_serials_dialog_unable_to_resolve_serials',
            shipmentLineItem: props.itemName,
          },
          extra: {
            serials: props.serials,
          },
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    props.isOpen,
    props.itemName,
    props.serials,
    shouldFallbackToAssetSearch,
  ]);

  // Lowercase each serial once so the filter never re-lowercases the whole
  // array on every search pass — only the query is lowercased per pass.
  const confirmedIndex = useMemo(
    () =>
      props.serials.map((serial) => ({
        serial,
        lower: serial.toLowerCase(),
      })),
    [props.serials]
  );
  const pendingIndex = useMemo(
    () =>
      pendingSerials.map((serial) => ({
        serial,
        lower: serial.toLowerCase(),
      })),
    [pendingSerials]
  );

  // Filter confirmed serials based on the debounced search query
  const filteredSerials = useMemo(() => {
    const query = debouncedQuery.toLowerCase();
    return confirmedIndex
      .filter((entry) => entry.lower.includes(query))
      .map((entry) => entry.serial);
  }, [confirmedIndex, debouncedQuery]);

  // Filter pending serials based on the debounced search query
  const filteredPendingSerials = useMemo(() => {
    const query = debouncedQuery.toLowerCase();
    return pendingIndex
      .filter((entry) => entry.lower.includes(query))
      .map((entry) => entry.serial);
  }, [pendingIndex, debouncedQuery]);

  // Combined, index-addressable list backing the virtualized grid. Confirmed
  // serials come first, then pending; in practice only one kind is present
  // since pending is hidden once confirmed serials exist.
  const entries = useMemo<SerialEntry[]>(
    () => [
      ...filteredSerials.map(
        (serial): SerialEntry => ({ serial, kind: 'confirmed' })
      ),
      ...filteredPendingSerials.map(
        (serial): SerialEntry => ({ serial, kind: 'pending' })
      ),
    ],
    [filteredSerials, filteredPendingSerials]
  );

  const totalCount = props.serials.length + pendingSerials.length;
  const hasPendingSerials = pendingSerials.length > 0;

  const rowCount = Math.ceil(entries.length / SERIALS_PER_ROW);
  const listHeight = Math.min(rowCount * ROW_HEIGHT_PX, MAX_LIST_HEIGHT_PX);

  const getAssetKey = (serial: string) => assetKeyMap[serial.toLowerCase()];

  // Debounced setter for the applied query; cancelled on unmount and on close
  const updateDebouncedQuery = useMemo(
    () =>
      debounce((value: string) => setDebouncedQuery(value), SEARCH_DEBOUNCE_MS),
    []
  );
  useEffect(() => () => updateDebouncedQuery.cancel(), [updateDebouncedQuery]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    updateDebouncedQuery(value);
  };

  // Reset search and asset key map when dialog closes
  const handleClose = () => {
    updateDebouncedQuery.cancel();
    setSearchQuery('');
    setDebouncedQuery('');
    setAssetKeyMap({});
    setDidResolverFallbackToAssetSearch(false);
    props.onClose();
  };

  // Export serials to CSV
  const handleExportCSV = useCallback(() => {
    const headers = ['Serial', 'Status'];
    const rows: string[][] = [];

    for (const serial of props.serials) {
      rows.push([escapeCSV(serial), 'Confirmed']);
    }

    for (const serial of pendingSerials) {
      rows.push([escapeCSV(serial), 'Pending']);
    }

    const csvContent = buildCSVContent(headers, rows);
    const filename = generateCSVFilename(`serials-${props.itemName}`);
    triggerCSVDownload(csvContent, filename);
  }, [props.serials, pendingSerials, props.itemName]);

  // Render a single serial cell (confirmed or pending) within a virtualized row
  const renderSerialCell = (entry: SerialEntry) => {
    const isConfirmed = entry.kind === 'confirmed';
    const assetKey = isConfirmed ? getAssetKey(entry.serial) : undefined;
    const tooltip =
      entry.serial.length > SERIAL_TOOLTIP_MIN_LENGTH
        ? entry.serial
        : undefined;

    return (
      <Stack
        key={`${entry.kind}-${entry.serial}`}
        direction="row"
        alignItems="center"
        gap={1}
        sx={{
          borderLeft: 3,
          borderRadius: '5px',
          borderColor: isConfirmed ? 'success.main' : 'info.main',
          pl: 1,
          py: 0.5,
          minWidth: 0,
        }}
      >
        {isConfirmed ? (
          <CheckOutlined
            fontSize="small"
            sx={{ color: 'success.main', flexShrink: 0 }}
          />
        ) : (
          <AccessTimeOutlined
            fontSize="small"
            sx={{ color: 'info.main', flexShrink: 0 }}
          />
        )}
        <ConditionalTooltip
          tooltipText={tooltip}
          arrow
          sx={{ flex: 1, minWidth: 0 }}
        >
          {isConfirmed && shouldFallbackToAssetSearch ? (
            <Link
              to="/assets"
              search={{ query: entry.serial }}
              underline="hover"
            >
              <SerialText serial={entry.serial} />
            </Link>
          ) : isConfirmed && assetKey ? (
            <Link
              to="/assets/$assetKey"
              params={{ assetKey }}
              underline="hover"
            >
              <SerialText serial={entry.serial} />
            </Link>
          ) : (
            <SerialText serial={entry.serial} />
          )}
        </ConditionalTooltip>
      </Stack>
    );
  };

  return (
    <Dialog open={props.isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" component="span">
          Serials for {props.itemName}
        </Typography>
        <IconButton onClick={handleClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Summary and search */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Total: {formatNumber(totalCount)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                size="small"
                sx={{ width: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip arrow title="Export to CSV">
                <IconButton
                  onClick={handleExportCSV}
                  size="small"
                  aria-label="Export to CSV"
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Pending serials notice */}
          {hasPendingSerials && (
            <Typography variant="body2" color="info.main">
              Pending serials are preliminary and may change before the shipment
              is finalized
            </Typography>
          )}

          {/* Serial list */}
          {entries.length === 0 ? (
            <Box
              sx={{
                minHeight: '100px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No serials found
              </Typography>
            </Box>
          ) : (
            <FixedSizeList
              height={listHeight}
              width="100%"
              itemSize={ROW_HEIGHT_PX}
              itemCount={rowCount}
              overscanCount={OVERSCAN_ROW_COUNT}
            >
              {({ index, style }: ListChildComponentProps) => {
                const startIdx = index * SERIALS_PER_ROW;
                const rowEntries = entries.slice(
                  startIdx,
                  startIdx + SERIALS_PER_ROW
                );
                return (
                  <Box
                    style={style}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                      gap: 2,
                      pr: 1,
                    }}
                  >
                    {rowEntries.map((entry) => renderSerialCell(entry))}
                  </Box>
                );
              }}
            </FixedSizeList>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// Single-line, ellipsized serial label shared by linked and plain cells
function SerialText(props: { serial: string }) {
  return (
    <Typography
      variant="body2"
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {props.serial}
    </Typography>
  );
}
